import { useEffect, useState, useRef } from 'react';
import { log } from '@/lib/logger';
import { Clock } from 'lucide-react';
import { fetchCoachProgress } from '@/lib/api/coach';
import { resolveStepStatus } from '@/utils/resolveStepStatus';
import type { CoachProgressResponse, CoachStep } from '@/types/coachProgress';
import type { PlanBuildProgress } from '@/types/planBuildProgress';
import { Button } from '@/components/ui/button';
import { classifyApiError } from '@/lib/api/errorClassification';
import { PlanBuildProgressCard } from './PlanBuildProgressCard';
import { PlannerReviewCard, type PlannerReviewStep } from './PlannerReviewCard';

type CoachMode = 'idle' | 'awaiting_intent' | 'planning' | 'executing' | 'done';

interface CoachProgressPanelProps {
  conversationId: string | null;
  mode?: CoachMode;
  /** Proposed steps from backend (planner.steps or action_plan) for planning mode. */
  proposedSteps?: CoachStep[];
  onConfirm?: () => void;
  onComplete?: () => void;
}

export function CoachProgressPanel({
  conversationId,
  mode = 'executing',
  proposedSteps,
  onConfirm,
  onComplete,
}: CoachProgressPanelProps) {
  const [progress, setProgress] = useState<CoachProgressResponse | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [hasCompleted, setHasCompleted] = useState(false);
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const completionNotifiedRef = useRef(false);
  const hadOutboundProgressRef = useRef(false);
  const lastOutboundProgressRef = useRef<PlanBuildProgress | null>(null);
  const [fadingOutProgress, setFadingOutProgress] = useState<PlanBuildProgress | null>(null);
  const consecutiveIdleRef = useRef(0);
  const MAX_IDLE_POLLS = 3; // Stop after 3 consecutive empty responses (~5s)
  const fadeTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Stable ref for onComplete to avoid restarting polling on parent re-renders
  const onCompleteRef = useRef(onComplete);
  onCompleteRef.current = onComplete;

  // Only fetch progress when executing (active conversation)
  useEffect(() => {
    if (!conversationId || mode !== 'executing') {
      if (!conversationId) {
        log.debug('COACH', 'Skipping polling: conversationId is', conversationId);
      } else if (mode !== 'executing') {
        log.debug('COACH', 'Skipping polling: mode is', mode, '(expected "executing")');
      }
      // Clear any existing interval when not executing
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
      setIsLoading(false);
      setProgress(null);
      setHasCompleted(false);
      setFadingOutProgress(null);
      completionNotifiedRef.current = false;
      hadOutboundProgressRef.current = false;
      lastOutboundProgressRef.current = null;
      if (fadeTimeoutRef.current) {
        clearTimeout(fadeTimeoutRef.current);
        fadeTimeoutRef.current = null;
      }
      return;
    }
    
    log.debug('COACH', 'Starting polling for conversation:', conversationId);
    log.debug('COACH', 'Polling endpoint: /conversations/' + conversationId + '/progress');
    setIsLoading(true);
    consecutiveIdleRef.current = 0;

    const fetchProgress = async () => {
      try {
        log.debug('COACH', 'Polling /conversations/' + conversationId + '/progress');
        const data = await fetchCoachProgress(conversationId);
        const hasOutbound = data.outbound_progress != null;
        if (hasOutbound) {
          lastOutboundProgressRef.current = data.outbound_progress;
        }
        setProgress(data);
        setIsLoading(false);

        // Backend says idle: stop polling immediately (no more 3-poll wait).
        if (data.idle === true) {
          log.debug('COACH', 'Stopping polling: backend returned idle=true');
          if (intervalRef.current) {
            clearInterval(intervalRef.current);
            intervalRef.current = null;
          }
          return;
        }

        // Legacy idle detection: stop after N consecutive empty responses if backend did not send idle.
        const isIdle = data.steps.length === 0 && !data.outbound_progress && !hadOutboundProgressRef.current;
        if (isIdle) {
          consecutiveIdleRef.current += 1;
          if (consecutiveIdleRef.current >= MAX_IDLE_POLLS) {
            log.debug('COACH', 'Stopping polling: no active task after', MAX_IDLE_POLLS, 'polls');
            if (intervalRef.current) {
              clearInterval(intervalRef.current);
              intervalRef.current = null;
            }
            return;
          }
        } else {
          consecutiveIdleRef.current = 0;
        }

        // Plan build done when backend clears outbound_progress (signal-driven)
        if (hadOutboundProgressRef.current && !hasOutbound) {
          hadOutboundProgressRef.current = false;
          const lastOutbound = lastOutboundProgressRef.current;
          lastOutboundProgressRef.current = null;
          if (lastOutbound) {
            setFadingOutProgress(lastOutbound);
            if (fadeTimeoutRef.current) clearTimeout(fadeTimeoutRef.current);
            fadeTimeoutRef.current = setTimeout(() => {
              setFadingOutProgress(null);
              fadeTimeoutRef.current = null;
            }, 200);
          }
          setHasCompleted(true);
          if (intervalRef.current) {
            clearInterval(intervalRef.current);
            intervalRef.current = null;
          }
          if (!completionNotifiedRef.current && onCompleteRef.current) {
            completionNotifiedRef.current = true;
            onCompleteRef.current();
          }
        } else {
          hadOutboundProgressRef.current = hasOutbound;
        }

        // Also check if all steps are completed (legacy path)
        const allCompleted = data.steps.length > 0 && data.steps.every((step) => {
          const status = resolveStepStatus(step.id, data.events);
          return status === 'completed' || status === 'failed' || status === 'skipped';
        });

        if (allCompleted) {
          setHasCompleted(true);
          
          // Stop polling
          if (intervalRef.current) {
            clearInterval(intervalRef.current);
            intervalRef.current = null;
          }
          
          // Notify parent of completion (only once)
          if (!completionNotifiedRef.current && onCompleteRef.current) {
            completionNotifiedRef.current = true;
            onCompleteRef.current();
          }
        }
      } catch (error: unknown) {
        log.error('COACH', 'Failed to fetch progress:', error);
        setIsLoading(false);
        
        // Use error classification to determine if we should stop
        const classified = classifyApiError(error);
        
        // TERMINAL errors (404, 403, 401) → stop polling immediately
        if (classified.class === 'TERMINAL') {
          log.debug('COACH', 'Terminal error detected, stopping polling:', classified);
          if (intervalRef.current) {
            clearInterval(intervalRef.current);
            intervalRef.current = null;
          }
        }
        // RETRYABLE errors → continue polling (interval will retry)
        // USER_ACTION errors → stop and surface to UI
        else if (classified.class === 'USER_ACTION') {
          log.warn('COACH', 'User action required, stopping polling:', classified);
          if (intervalRef.current) {
            clearInterval(intervalRef.current);
            intervalRef.current = null;
          }
        }
      }
    };

    // Initial fetch
    fetchProgress();

    // Poll every 1.5-2s (using 1750ms as a middle ground)
    intervalRef.current = setInterval(() => {
      fetchProgress();
    }, 1750);

    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
      if (fadeTimeoutRef.current) {
        clearTimeout(fadeTimeoutRef.current);
        fadeTimeoutRef.current = null;
      }
    };
  }, [conversationId, mode]);

  // Planning mode: only the confirm action (no steps checklist)
  if (mode === 'planning') {
    return onConfirm ? (
      <div className="mb-4">
        <Button
          onClick={onConfirm}
          className="w-full bg-coach hover:bg-coach/90 text-coach-foreground"
        >
          Generate Weekly Plan
        </Button>
      </div>
    ) : null;
  }

  // Hide when not executing (planning branch handled above)
  if (mode !== 'executing') {
    return null;
  }

  // When completed: keep checklist visible with all steps marked completed (cursor-style todo list)
  const forceCompleted = hasCompleted;

  // Show loading state while fetching initial progress (only when not yet completed)
  if (!forceCompleted && isLoading && !progress) {
    return (
      <div className="mb-4 p-4 bg-accent/5 border border-accent/20 rounded-lg">
        <div className="space-y-2">
          <div className="flex items-center gap-3 text-sm text-muted-foreground">
            <Clock className="h-4 w-4 animate-pulse" />
            <span>Initializing training plan...</span>
          </div>
        </div>
      </div>
    );
  }

  // Signal-driven plan-build progress: show 4 stages when backend has outbound_progress (no timers)
  const displayOutbound = progress?.outbound_progress ?? fadingOutProgress;
  if (displayOutbound != null) {
    const isFadingOut = fadingOutProgress != null;
    return (
      <div
        className={`mb-4 p-4 bg-accent/5 border border-accent/20 rounded-lg transition-opacity duration-200 ${isFadingOut ? 'opacity-0' : 'opacity-100'}`}
      >
        <PlanBuildProgressCard outboundProgress={displayOutbound} />
      </div>
    );
  }

  // Show empty/initializing state if no steps yet (only when not completed)
  if (!forceCompleted && (!progress || progress.steps.length === 0)) {
    return (
      <div className="mb-4 p-4 bg-accent/5 border border-accent/20 rounded-lg">
        <div className="space-y-2">
          <div className="flex items-center gap-3 text-sm text-muted-foreground">
            <Clock className="h-4 w-4 animate-pulse" />
            <span>Preparing your training plan...</span>
          </div>
        </div>
      </div>
    );
  }

  // When completed, we may have progress from last poll; if not, show nothing (shouldn't happen)
  const rawSteps = progress?.steps ?? [];
  if (rawSteps.length === 0) return null;

  const steps: PlannerReviewStep[] = rawSteps.map((step) => ({
    id: step.id,
    label: step.label,
    status: forceCompleted ? ('completed' as const) : resolveStepStatus(step.id, progress!.events),
  }));

  return (
    <div className="mb-4">
      <PlannerReviewCard
        title="Weekly Training Plan — Review Required"
        steps={steps}
      />
    </div>
  );
}
